import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Button,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import {KeyChip} from "../../components/key-chip/KeyChip";
import {NfcScanIcon} from "../../components/nfc-scan-icon/NfcScanIcon";
import {KeyData} from "../../api/Model";
import {activeRecord, useKeyDataStore} from "../../api/KeyDataStore";
import {listenCustomWindowEvent, useEffectWithLifetime} from "../../util/Lifetime";
import {NFC_KEY_DETECTED_EVENT, NFC_KEY_READING_EVENT} from "../../util/NfcReaderListener";
import {SetState} from "../../util/FormHandlers";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {parseCsv} from "../../util/CSVReadUtil";

export interface NfcDataRow {
    keyNumber: string;
    uploadedTransponderId: string;
    resolvedKey?: KeyData;
}

function parseNfcCsv(text: string): Array<{ keyNumber: string; nfc: string }> {
    return parseCsv(text, {
        keyNumber: {
            title: 'Key Number',
            parse: value => value.trim()
        },
        nfc: {
            title: 'NFC',
            parse: value => value.trim()
        },
    }).filter(row => row.nfc);
}

export function UploadNfcDataForm({form, setForm}: {
    form: NfcDataRow[];
    setForm: SetState<NfcDataRow[]>;
}) {
    const allKeysRV = useKeyDataStore(state => state.remoteValue);

    const [parseError, setParseError] = useState<string>();
    const [highlightedKeyNumber, setHighlightedKeyNumber] = useState<string>();
    const [nfcReading, setNfcReading] = useState(false);
    const [nfcReadingError, setNfcReadingError] = useState<string>();
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

    const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const csvFileContent = e.target?.result as string;

                const allKeys = await allKeysRV.value;

                const nfcDataRow: NfcDataRow[] = parseNfcCsv(csvFileContent)
                    .map(({keyNumber, nfc}) => ({
                        keyNumber,
                        uploadedTransponderId: nfc,
                        resolvedKey: allKeys.find(k => k.keyNumber === keyNumber),
                    }));

                setForm(nfcDataRow);
                setParseError(undefined);
                setNfcReadingError(undefined);
            } catch (err: any) {
                setParseError(err.message || 'Failed to parse CSV');
                setForm([]);
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    }, [allKeysRV, setForm, setNfcReadingError]);

    useEffectWithLifetime(lifetime => {
        listenCustomWindowEvent(lifetime, NFC_KEY_DETECTED_EVENT, (event: CustomEvent<string>) => {
            const scanned = event.detail;
            setNfcReading(false);
            const matchingRow = form.find(row => row.uploadedTransponderId.toUpperCase() === scanned.toUpperCase());
            if (matchingRow) {
                setHighlightedKeyNumber(matchingRow.keyNumber);
                setNfcReadingError(undefined);
                rowRefs.current.get(matchingRow.keyNumber)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            } else {
                setNfcReadingError('Scanned key not found in uploaded data');
            }
        });
        listenCustomWindowEvent(lifetime, NFC_KEY_READING_EVENT, (event: CustomEvent<number>) => {
            setNfcReading(event.detail > 0);
        });
    }, [form]);

    useEffect(() => {
        if (highlightedKeyNumber === undefined) return;
        const timeout = setTimeout(() => setHighlightedKeyNumber(undefined), 3000);
        return () => clearTimeout(timeout);
    }, [highlightedKeyNumber]);

    return (
        <Stack spacing={2}>
            {form.length > 0 ? (
                <Stack direction="row" alignItems="center" spacing={2}>
                    <NfcScanIcon pulse={nfcReading} error={nfcReadingError}/>
                    {nfcReadingError ? (
                        <Typography variant="body2" color="error">{nfcReadingError}</Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">Scan key with NFC reader to highlight
                            matching key</Typography>
                    )}
                </Stack>
            ) : (
                <Stack direction="row" justifyContent="center">
                    <Button variant="outlined" component="label" startIcon={<UploadIcon/>}>
                        Upload CSV File
                        <input type="file" accept=".csv" hidden onChange={handleFileUpload}/>
                    </Button>
                </Stack>
            )}

            {parseError && <Alert severity="error">{parseError}</Alert>}

            {form.length > 0 && (
                <TableContainer component={Paper} sx={{maxHeight: 400}}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width="150">Key</TableCell>
                                <TableCell>Transponder ID (NFC)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.map(row => {
                                const record = row.resolvedKey ? activeRecord(row.resolvedKey) : undefined;
                                const isHighlighted = highlightedKeyNumber === row.keyNumber;
                                return (
                                    <TableRow
                                        key={row.keyNumber}
                                        ref={el => {
                                            if (el) rowRefs.current.set(row.keyNumber, el);
                                            else rowRefs.current.delete(row.keyNumber);
                                        }}
                                        sx={isHighlighted ? {
                                            backgroundColor: 'action.selected',
                                            transition: 'background-color 0.2s',
                                        } : {
                                            transition: 'background-color 0.5s',
                                        }}
                                    >
                                        <TableCell>
                                            {row.resolvedKey && record ? (
                                                <KeyChip keyData={row.resolvedKey} record={record}/>
                                            ) : (
                                                <Tooltip title="Unresolved key">
                                                    <Chip color="error" label={row.keyNumber} size="small"
                                                          icon={<ErrorOutlineIcon/>}/>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Typography variant="caption">
                                                    {row.uploadedTransponderId}
                                                </Typography>
                                                {row.resolvedKey?.transponderId && row.resolvedKey?.transponderId !== row.uploadedTransponderId && (
                                                    <Typography variant="caption" color="error">
                                                        Current NFC: {row.resolvedKey.transponderId}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Stack>
    );
}
